import {
  autoGenerate,
  cancel,
  checkNumber,
  complete,
  index,
  post,
  put,
  get,
} from "src/api/ewb";

import EWayBillRequestForm from "src/modules/purchase-order/components/forms/EWayBillRequestForm.vue";
import EWayBillForm from "src/modules/accounts/components/forms/EWayBillForm.vue";
import { findIndex } from "src/assets/scripts/utilities/helpers";
import { usePurchaseOrderStore } from "./purchase-order";

const getInitialState = () => {
  return {
    eWayBills: [],
    eWayBill: {},
  };
};

export const useEWayBillStore = defineStore("ewb", () => {
  const { state: ewayBillRequestFormState, toggle: toggleEWayBillRequestForm } =
    useDrawer({
      component: EWayBillRequestForm,
    });

  const { state: ewayBillFormState, toggle: toggleEWayBillForm } = useDrawer({
    component: EWayBillForm,
  });

  let state = reactive(getInitialState());

  const setEWayBill = (updatedEWayBill) => {
    state.eWayBills[findIndex(state.eWayBills, updatedEWayBill, "_id")] =
      updatedEWayBill;

    state.eWayBill = updatedEWayBill;
  };

  const autoGenerateEWayBill = async (ewb) => {
    try {
      const response = await autoGenerate({ _id: ewb._id });

      await getEWayBills();

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const cancelEWayBill = async (params) => {
    try {
      const response = await cancel(params);

      const { getCounts } = useAccountStore();
      const { getPurchaseOrderFiles } = usePurchaseOrderStore();

      await Promise.all([getCounts(), getPurchaseOrderFiles(), getEWayBills()]);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const completeEWayBill = async (eWayBill) => {
    try {
      const response = await complete(eWayBill);

      const { getCounts } = useAccountStore();

      await Promise.all([getCounts(), getEWayBills()]);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const getEWayBill = async ({ id, source = "kam" }) => {
    try {
      const response = await get({ id, source });

      state.eWayBill = response?.data?.data;

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const filters = ref({
    po_id: null,
    stages: null,
  });

  const { filter, pagination, params } = usePagination(filters);

  const getEWayBills = async (options = {}) => {
    try {
      options = unref(options);

      if (options?.filters) {
        filters.value = options?.filters;

        delete options.filters;
      }

      const response = await index({
        params: unref(params),
        ...options,
      });

      state.eWayBills = response.data?.data?.data;

      pagination.value.rowsNumber = response.data?.data?.count;

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const postEWayBill = async (eWayBill) => {
    try {
      const response = await post(eWayBill);

      await getEWayBills();

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const putEWayBill = async (eWayBill) => {
    try {
      const response = await put(eWayBill);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const addFile = async () => {
    await getEWayBills({
      filters: { ...unref(filters), stage: "Not Started" },
    });
  };

  const updateFile = (file) => {
    const ewbIndex = findIndex(state.eWayBills, { _id: file.bill_id }, "_id");

    const fileIndex = findIndex(
      state.eWayBills[ewbIndex].files,
      { _id: file._id },
      "_id",
    );

    if (ewbIndex !== -1 && fileIndex !== -1) {
      if (file.status === "Active") {
        state.eWayBills[ewbIndex].files[fileIndex] = file;
      } else {
        state.eWayBills[ewbIndex].files.splice(fileIndex, 1);
      }
    }
  };

  const isDuplicate = async (number) => {
    const response = await checkNumber(number);

    return response;
  };

  const reset = () => {
    Object.assign(state, getInitialState());
  };

  return {
    ewayBillFormState,
    ewayBillRequestFormState,

    toggleEWayBillForm,
    toggleEWayBillRequestForm,

    filter,
    filters,
    pagination,

    ...toRefs(state),

    setEWayBill,

    addFile,
    updateFile,

    getEWayBill,
    getEWayBills,
    postEWayBill,
    putEWayBill,

    autoGenerateEWayBill,
    cancelEWayBill,
    completeEWayBill,
    isDuplicate,

    reset,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useEWayBillStore, import.meta.hot));
}
